import type { BuilderCommunityMapConfig } from "api-types/builders/salesConfig";
import type { LotBadgeSymbol, LotSpec, LotStatus } from "api-types/lots/lotTypes";
import { getContrastTextColor } from "common/utils/color";
import { getLotSpecLabel } from "lib/components/builders/communities/utils/lotSpecLabels";
import colors from "ui/styles/colors";

interface LotBadgeColor {
  fill: string;
  text: string;
}

export const DEFAULT_LOT_BADGE_COLORS: Record<LotBadgeSymbol, LotBadgeColor> = {
  available: { fill: colors.greenAccessible, text: colors.white },
  spec: { fill: colors.blueAccessible, text: colors.white },
  model: { fill: colors.purpleAccessible, text: colors.white },
  pending: { fill: colors.yellowAccessible, text: colors.black },
  sold: { fill: colors.orangeAccessible, text: colors.white },
  closed: { fill: colors.pinkAccessible, text: colors.white },
  unavailable: { fill: colors.grayAccessible, text: colors.black },
};

export const DEFAULT_LOT_BADGE_LABELS: Record<LotBadgeSymbol, string> = {
  available: "Available",
  spec: "Spec",
  model: "Model",
  pending: "Pending",
  sold: "Sold",
  closed: "Closed",
  unavailable: "Unavailable",
};

type LotBadgeInput = { lotStatus: LotStatus; spec: LotSpec };

export function getLotBadgeSymbol(lot: LotBadgeInput): LotBadgeSymbol {
  if (lot.lotStatus === "available" && lot.spec === "spec") {
    return "spec";
  }
  if (lot.lotStatus !== "closed" && lot.spec === "model") {
    return "model";
  }
  return lot.lotStatus;
}

/** Resolves the display label for a badge symbol, using builder overrides when set. */
export function getLotBadgeLabel(symbol: LotBadgeSymbol, communityMapConfig?: BuilderCommunityMapConfig): string {
  if (symbol === "spec" || symbol === "model") {
    return getLotSpecLabel(symbol, communityMapConfig);
  }
  return communityMapConfig?.lotStatusLabels?.[symbol]?.trim() || DEFAULT_LOT_BADGE_LABELS[symbol];
}

/** Resolves the fill color for a badge symbol, using a builder override when set. */
export function getLotBadgeFillColor(symbol: LotBadgeSymbol, communityMapConfig?: BuilderCommunityMapConfig): string {
  return communityMapConfig?.lotStatusColors?.[symbol] || DEFAULT_LOT_BADGE_COLORS[symbol].fill;
}

/** Resolves label, fill color, and auto-contrast text color for a badge symbol. */
export function getLotBadgeDisplay(
  symbol: LotBadgeSymbol,
  communityMapConfig?: BuilderCommunityMapConfig,
): { label: string; fill: string; text: string } {
  const fill = getLotBadgeFillColor(symbol, communityMapConfig);
  return {
    label: getLotBadgeLabel(symbol, communityMapConfig),
    fill,
    text: getContrastTextColor(fill),
  };
}

/** Whether a badge symbol should be omitted from the map legend. */
export function isLotBadgeHiddenFromLegend(
  symbol: LotBadgeSymbol,
  communityMapConfig?: BuilderCommunityMapConfig,
): boolean {
  return communityMapConfig?.lotStatusHidden?.[symbol] === true;
}

export function getLotFillColor(lot: LotBadgeInput, communityMapConfig?: BuilderCommunityMapConfig): string {
  return getLotBadgeFillColor(getLotBadgeSymbol(lot), communityMapConfig);
}

export function getLotBadgeTextColor(lot: LotBadgeInput, communityMapConfig?: BuilderCommunityMapConfig): string {
  return getContrastTextColor(getLotFillColor(lot, communityMapConfig));
}
